import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkles, TrendingUp, ClipboardCheck, BarChart3, FileText, ShieldCheck, ArrowRight } from "lucide-react";
import { Button } from "../components/ui/button";

const features = [
  { icon: Sparkles, title: "AI Paper Generator", desc: "Original exam papers matching real question style, command words and mark allocation — never copied.", span: "md:col-span-7" },
  { icon: TrendingUp, title: "Prediction Engine", desc: "Statistical models over years of past papers reveal which topics are most likely to appear next.", span: "md:col-span-5" },
  { icon: ClipboardCheck, title: "AI Examiner", desc: "Upload answers and get examiner-quality marking, mark-by-mark justification and an estimated grade.", span: "md:col-span-5" },
  { icon: BarChart3, title: "Revision Analytics", desc: "Track weakest topics, average scores and time revised. AI tells you exactly what to revise next.", span: "md:col-span-7" },
];

export default function Landing() {
  const navigate = useNavigate();
  return (
    <div data-testid="landing-page">
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 hero-glow pointer-events-none" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-24 pb-20">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="max-w-3xl">
            <div className="inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.2em] text-primary mb-8">
              <Sparkles className="h-3 w-3" /> AI-Powered Revision
            </div>
            <h1 className="text-5xl md:text-6xl font-bold tracking-tighter leading-none">
              Revise smarter <span className="bg-gradient-to-r from-indigo-400 to-purple-400 bg-clip-text text-transparent">with AI.</span>
            </h1>
            <p className="mt-6 text-base md:text-lg text-muted-foreground leading-relaxed max-w-2xl">
              Generate unlimited exam papers, receive examiner-quality marking, and discover data-driven predictions based on years of previous exams.
            </p>
            <div className="mt-10 flex flex-wrap gap-3">
              <Button size="lg" data-testid="get-started-btn" onClick={() => navigate("/signup")} className="rounded-full glow-btn px-8">
                Get Started <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
              <Button size="lg" variant="outline" data-testid="hero-signup-btn" onClick={() => navigate("/signup")} className="rounded-full px-8">Sign Up</Button>
              <Button size="lg" variant="ghost" data-testid="hero-login-btn" onClick={() => navigate("/login")} className="rounded-full px-8">Login</Button>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 pb-24">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
          {features.map((f, i) => (
            <motion.div key={f.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}
              className={`${f.span} card-lift rounded-3xl border border-border bg-card p-8`} data-testid={`landing-feature-${i}`}>
              <div className="h-11 w-11 rounded-2xl bg-gradient-to-br from-indigo-500/20 to-purple-500/20 flex items-center justify-center mb-5">
                <f.icon className="h-5 w-5 text-primary" />
              </div>
              <h3 className="text-xl font-semibold tracking-tight mb-2">{f.title}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{f.desc}</p>
            </motion.div>
          ))}
        </div>
        <div className="mt-16 rounded-3xl border border-border bg-card p-8 flex flex-col md:flex-row items-start md:items-center gap-4">
          <ShieldCheck className="h-6 w-6 text-primary shrink-0" />
          <p className="text-sm text-muted-foreground leading-relaxed">
            PsycGenie analyses historical papers for patterns only. Every generated question is completely original — we never reproduce copyrighted exam content. Predictions are probability estimates, not guarantees.
          </p>
        </div>
      </section>

      <footer className="border-t border-border py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 flex items-center justify-between text-xs text-muted-foreground">
          <span className="flex items-center gap-2"><FileText className="h-3.5 w-3.5" /> PsycGenie — AI Exam Revision Platform</span>
          <span>Built for students, by data.</span>
        </div>
      </footer>
    </div>
  );
}
